<script lang="ts">
	import { getFolderPermissions } from "./permissions";
	import { fade } from "svelte/transition";
	import { getJson, postJson, ServerError } from "../http";
	import { getFoldersInfo } from "./folders";
	import ErrorModal from "../modal/ErrorModal.svelte";
	import { getConfirmationStore } from "../modal/confirmationStore";
	import type { DashboardRecentNewslettersQuery } from "$gql/generated";
	import { getClassicWebUrl, getCurrentUser, getIsInsideConnect } from "../currentUser";

	const errorCodesToText: Record<string, string> = {};

	export let newsletter: DashboardRecentNewslettersQuery["me"]["newsletters"]["recent"][0];

	const currentUser = getCurrentUser();
	const isInsideConnect = getIsInsideConnect();
	const foldersInfo = getFoldersInfo();
	const confirmationStore = getConfirmationStore();
	const CLASSIC_WEB_URL = getClassicWebUrl();
	let showOptions = false;
	let showAddToFolder = false;
	let errorMsg: string | undefined = undefined;
	let errorTitle: string | undefined = undefined;
	let postInAction: string | undefined = undefined;

	$: permissions = getFolderPermissions();

	function duplicateClicked() {
		confirmationStore.showConfirmation(
			"Duplicate newsletter",
			"Create a copy of this newsletter and edit the copy?",
			"Duplicate",
			doDuplicate
		);
	}

	async function doDuplicate() {
		try {
			postInAction = "Duplicating...";
			const data = {
				id: newsletter.id
			};
			$confirmationStore.confirmButtonText = "Duplicating...";
			confirmationStore.disable();
			const editNewsletterUrl = await postJson<any>(`${CLASSIC_WEB_URL}/app/pages/duplicate_json`, data);
			document.location.href = editNewsletterUrl;
		} catch (e) {
			const err = e as any;
			if (err.code === "no_credits") {
				// setTimeout is needed in order to allow the previous confirmation process to complete and show the new message
				// Otherwise the confirmation will not be shown
				setTimeout(() => {
					if (currentUser.organization?.isArchived) {
						confirmationStore.showConfirmation(
							"You're out of newsletter credits 😢",
							`Your Account has been Archived 😔
              If you’d like to create more newsletters, contact Smore below.`,
							"Contact Smore",
							doContactSmore
						);
					} else {
						confirmationStore.showConfirmation(
							"You're out of newsletter credits 😢",
							"You are currently on a Free Trial, and can’t create or duplicate any more newsletters. Upgrade now and get more newsletters, enhanced analytics, education backgrounds and more!",
							"Upgrade",
							doUpgrade
						);
					}
				}, 30);
			} else {
				errorTitle = "Error duplicating newsletter";
				errorMsg = errorCodesToText[err.code] || ServerError.GENERIC_ERROR;
			}
		} finally {
			confirmationStore.enable();
			postInAction = undefined;
		}
	}

	async function doUpgrade() {
		window.open(`${CLASSIC_WEB_URL}/pricing?ur=free_user_nl_credits`, "_blank");
	}

	async function doContactSmore() {
		window.open(`${CLASSIC_WEB_URL}/contact-renewal?t=archived&s=duplicate`, "_blank");
	}

	async function deleteClicked() {
		if (currentUser.caps.disable_confirmation_in_newsletter_card_overlay) {
			console.log("disable_confirmation_in_newsletter_card_overlay flag is ON, skipping `delete` confirmation");
			return doDelete();
		}
		confirmationStore.showConfirmation(
			"Delete newsletter",
			"Checking to see if this newsletter has any Scheduled Deliveries planned....",
			"Delete",
			console.log
		);
		confirmationStore.disable();

		const numberOfScheduledDeliveries = await getJson(`${CLASSIC_WEB_URL}/app/newsletters/check_scheduled_deliveries/${newsletter.id}`);
		let message = "Are you sure you want to delete this newsletter?";
		if (numberOfScheduledDeliveries === 1) {
			message = `<i>Uh oh!</i> This newsletter has future plans.<br/><br/>Are you sure you want to delete this newsletter and cancel all of its scheduled deliveries?`;
		}
		confirmationStore.showConfirmation("Delete newsletter", message, "Delete", doDelete);
	}

	async function doDelete() {
		try {
			postInAction = "Deleting...";
			await postJson(`${CLASSIC_WEB_URL}/app/pages/delete_json`, {
				id: newsletter.id
			});
			document.location.reload();
		} catch (e) {
			const err = e as any;
			errorTitle = "Error deleting newsletter";
			if (err.code && err.code.startsWith("concurrent_edit:")) {
				const editingUserName = err.code.replace("concurrent_edit:", "");
				errorMsg = `Cannot delete newsletter. ${editingUserName} is currently working on it.`;
			} else {
				errorMsg = errorCodesToText[err.code] || ServerError.GENERIC_ERROR;
			}
		} finally {
			postInAction = undefined;
		}
	}

	async function removeClicked() {
		if (currentUser.caps.disable_confirmation_in_newsletter_card_overlay) {
			console.log("disable_confirmation_in_newsletter_card_overlay flag is ON, skipping `remove` confirmation");
			return doRemove();
		}
		confirmationStore.showConfirmation(
			"Remove newsletter from folder",
			"Remove this newsletter from the current folder?",
			"Remove",
			doRemove
		);
	}

	async function doRemove() {
		try {
			postInAction = "Removing...";
			await postJson(`${CLASSIC_WEB_URL}/app/page_folders/remove_page_id_from_folder_json`, {
				page_id: newsletter.id,
				folder_id: $permissions.folderId
			});
			document.location.reload();
		} catch (e) {
			errorTitle = "Error removing newsletter";
			errorMsg = errorCodesToText[(e as any).code] || ServerError.GENERIC_ERROR;
		} finally {
			postInAction = undefined;
		}
	}

	async function privacyClicked() {
		if (currentUser.caps.disable_confirmation_in_newsletter_card_overlay) {
			console.log("disable_confirmation_in_newsletter_card_overlay flag is ON, skipping `privacy` confirmation");
			return doChangePrivacy();
		}
		confirmationStore.showConfirmation(
			"Change newsletter privacy",
			newsletter.privacy === "public" ? "Make this newsletter private?" : "Make this newsletter public?",
			"Change",
			doChangePrivacy
		);
	}

	async function doChangePrivacy() {
		try {
			const newPrivacy = newsletter.isPublic ? "unlisted" : "public";
			postInAction = "Updating privacy...";
			await postJson(`${CLASSIC_WEB_URL}/app/pages/change_privacy_json`, {
				id: newsletter.id,
				privacy: newPrivacy
			});
			newsletter.isPublic = newsletter.isPublic;
			document.location.reload();
		} catch (e) {
			const err = e as any;
			errorTitle = "Error changing privacy";
			errorMsg = errorCodesToText[err.code] || ServerError.GENERIC_ERROR;
		} finally {
			postInAction = undefined;
		}
	}

	function addToFolderClicked() {
		foldersInfo && foldersInfo.showAddToFolder(newsletter.id);
	}

	function restoreToFolderClicked() {
		foldersInfo && foldersInfo.showRestoreToFolder(newsletter.id);
	}
</script>

<ErrorModal bind:errorMsg title={errorTitle} />

<div class="absolute left-0 right-0 top-0 z-30 flex items-center justify-between p-3 text-sm">
	{#if $permissions.can_edit_newsletters && !isInsideConnect}
		<a
			class="overlay-menu-item-background overlay-menu-item-color clickbale justift-start flex items-center rounded border border-transparent px-2 py-1 text-sm transition-colors"
			href={newsletter.urls.edit}>
			<span class="material-icons mr-1 text-sm">edit</span>
			Edit
		</a>
	{:else}
		<div />
	{/if}
	<button
		class="clickable overlay-menu-item-background overlay-menu-item-color z-20 rounded-lg border border-transparent text-sm transition-colors"
		class:rounded-lg={isInsideConnect}
		class:px-3={isInsideConnect}
		class:py-2={isInsideConnect}
		class:rounded={!isInsideConnect}
		class:px-2={!isInsideConnect}
		class:py-1={!isInsideConnect}
		on:click|preventDefault|stopPropagation={() => (showOptions = !showOptions)}
		class:border-nxgray-200={showOptions}>
		Options
		{#if isInsideConnect}
			<span class="material-icons ml-1 text-sm">expand_more</span>
		{/if}
	</button>
</div>

{#if postInAction}
	<div
		class="pointer-events-none absolute inset-0 z-50 flex cursor-default items-center justify-center rounded bg-nxgray-500 bg-opacity-75 px-3">
		<div class="animate-pulse rounded-2xl border-2 border-gray-200 px-3 py-1 text-13 font-medium text-gray-100">
			{postInAction}
		</div>
	</div>
{/if}
{#if showOptions}
	<menu
		transition:fade={{ duration: 200 }}
		class="absolute inset-0 z-30 flex cursor-default flex-col items-center rounded p-4 pt-14"
		class:bg-black={showOptions && !isInsideConnect}
		class:bg-opacity-60={showOptions && !isInsideConnect}
		on:mouseleave|preventDefault|stopPropagation={() => {
			if (!showAddToFolder) {
				showOptions = false;
			}
		}}>
		<div class="mt-2.5 w-full overflow-hidden rounded-lg bg-white py-1 shadow">
			{#if $permissions.can_edit_newsletters && isInsideConnect}
				{#if newsletter.hasAnnouncement}
					<a
						class="clickable flex w-full items-center px-3 py-2 text-left text-sm hover:bg-[var(--overlay-menu-item-hover-background)]"
						href={newsletter.urls.edit}>
						<span class="material-icons mr-2 text-18 text-gray-600">edit</span>
						Edit Announcement
					</a>
				{:else}
					<a
						class="clickable flex w-full items-center px-3 py-2 text-left text-sm hover:bg-[var(--overlay-menu-item-hover-background)]"
						href={newsletter.urls.edit}>
						<span class="material-icons mr-2 text-18 text-gray-600">edit</span>
						Edit
					</a>
				{/if}
			{/if}
			{#if newsletter.isDeleted}
				<button
					class="clickable flex w-full items-center px-3 py-2 text-left text-sm hover:bg-[var(--overlay-menu-item-hover-background)]"
					on:click|preventDefault|stopPropagation={restoreToFolderClicked}>
					<span class="material-icons mr-2 text-18 text-gray-600">restore</span>
					Restore
				</button>
			{:else}
				<button
					class="clickable flex w-full items-center px-3 py-2 text-left text-sm hover:bg-[var(--overlay-menu-item-hover-background)]"
					on:click|preventDefault|stopPropagation={duplicateClicked}>
					<span class="material-icons mr-2 text-18 text-gray-600">file_copy</span>
					Duplicate
				</button>
			{/if}
			{#if !newsletter.isDraft && !newsletter.isDeleted && !isInsideConnect}
				<button
					class="clickable flex w-full items-center px-3 py-2 text-left text-sm hover:bg-[var(--overlay-menu-item-hover-background)]"
					on:click|preventDefault|stopPropagation={privacyClicked}>
					{#if newsletter.isPublic}
						<span class="material-icons mr-2 text-18 text-gray-600">visibility_off</span>
						Make private
					{:else}
						<span class="material-icons mr-2 text-18 text-gray-600">visibility</span>
						Make public
					{/if}
				</button>
			{/if}
			{#if $permissions.can_remove_newsletters}
				<button
					class="clickable flex w-full items-center px-3 py-2 text-left text-sm hover:bg-[var(--overlay-menu-item-hover-background)]"
					on:click|preventDefault|stopPropagation={removeClicked}>
					<span class="material-icons mr-2 text-18 text-gray-600">folder_off</span>
					Remove from folder
				</button>
			{/if}
			{#if foldersInfo && ($permissions.folderId === "my" || $permissions.folderId === "drafts")}
				<button
					class="clickable flex w-full items-center px-3 py-2 text-left text-sm hover:bg-[var(--overlay-menu-item-hover-background)]"
					on:click|preventDefault|stopPropagation={addToFolderClicked}>
					<span class="material-icons mr-2 text-18 text-gray-600">create_new_folder</span>
					Add to folder
				</button>
			{/if}
			{#if $permissions.can_delete_newsletters}
				<button
					class="clickable flex w-full items-center px-3 py-2 text-left text-sm text-red-600 hover:bg-[#FEF2F2]"
					on:click|preventDefault|stopPropagation={deleteClicked}>
					<span class="material-icons mr-2 text-18">delete</span>
					Delete
				</button>
			{/if}
		</div>
	</menu>
{/if}
